import React from 'react';

import BraintreeAuthorizer from '../BraintreeAuthorizer/BraintreeAuthorizer';

import SentryErrorBoundary from '@components/SentryErrorBoundary/SentryErrorBoundary';
import ConfigProvider from '@providers/ConfigProvider';
import ErrorContextProvider from '@providers/ErrorContextProvider';
import I18nProvider from '@providers/I18nProvider';
import Providers from '@providers/Providers';
import {
  Additional3dsInfo,
  CreditCardType,
  Environment,
  FraudPreventionRecommendation,
  Locale,
  PaymentMethod,
  ShopBundle,
} from '@types-internal';

export type PaymentAuthorizationSwitchProps = {
  errorContainerId: string;
  shopBundle: ShopBundle;
  domain: string;
  locale: Locale;
  currency: string;
  customerId: string;
  customerType: string;
  sid: string;
  paymentMethod: PaymentMethod;
  pspToken: string;
  braintreeNonceToken: string;
  braintreeBinData?: string;
  braintreeBin: string;
  shouldUseSimpleBin: boolean;
  amount: number;
  additional3dsInfo?: Additional3dsInfo;
  cardType: CreditCardType;
  isSubscriptionFirst: boolean;
  subscriptionFirstInterval: string;
  fraudPreventionRecommendation: FraudPreventionRecommendation;
  siteId: number;
  env: Environment;
};

/**
 * Non-UI component which handles the authorization with Braintree.
 */
const PaymentAuthorizationSwitch: React.FC<PaymentAuthorizationSwitchProps> = ({
  errorContainerId,
  shopBundle,
  domain,
  locale,
  paymentMethod,
  pspToken,
  braintreeNonceToken,
  braintreeBinData,
  braintreeBin,
  shouldUseSimpleBin,
  amount,
  additional3dsInfo,
  cardType,
  isSubscriptionFirst,
  subscriptionFirstInterval,
  customerType,
  sid,
  currency,
  customerId,
  fraudPreventionRecommendation,
  siteId,
  env,
}) => {
  const resolvedBin = shouldUseSimpleBin === true ? braintreeBin : (braintreeBinData as string);

  return (
    <ConfigProvider env={env} shopBundle={shopBundle}>
      <I18nProvider env={env} shopBundle={shopBundle} locale={locale}>
        <SentryErrorBoundary featureName="action-creator">
          <ErrorContextProvider errorContainerId={errorContainerId}>
            <Providers shopBundle={shopBundle} domain={domain} locale={locale}>
              {paymentMethod === 'BK' && (
                <BraintreeAuthorizer
                  token={pspToken}
                  nonce={braintreeNonceToken}
                  bin={resolvedBin}
                  amount={amount}
                  currency={currency}
                  customerId={customerId}
                  sid={sid}
                  customerType={customerType}
                  additional3dsInfo={additional3dsInfo}
                  cardType={cardType}
                  isSubscriptionFirst={isSubscriptionFirst}
                  subscriptionFirstInterval={subscriptionFirstInterval}
                  fraudPreventionRecommendation={fraudPreventionRecommendation}
                  siteId={siteId}
                />
              )}
            </Providers>
          </ErrorContextProvider>
        </SentryErrorBoundary>
      </I18nProvider>
    </ConfigProvider>
  );
};

export default PaymentAuthorizationSwitch;
